import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global navigation progress bar — shows a slim animated bar at the top
 * during route transitions (while lazy chunks load).
 */
const NavigationProgress = () => {
  const location = useLocation();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Skip if only hash changed (same pathname)
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;

    // Start progress on route change
    setVisible(true);
    setProgress(15);

    // Simulate progress ticking up
    timerRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 12;
      });
    }, 200);

    // Complete after a short delay (content should be rendered by then)
    timeoutRef.current = setTimeout(() => {
      if (timerRef.current) clearInterval(timerRef.current);
      setProgress(100);
      setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 300);
    }, 400);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [location.pathname]);

  if (!visible) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] h-[3px] pointer-events-none"
      role="progressbar"
      aria-valuenow={Math.round(progress)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className="h-full bg-primary transition-all duration-300 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export default NavigationProgress;
